import { EntityKind } from '../routes'
import RegionService from './RegionService'

export interface RecentEntry {
    kind: EntityKind
    id: string
    // IDs only exist in the region they were created in
    region: string
    at: number
    label?: string
    accountId?: string
    accountNumber?: string
    userId?: string
}

const STORAGE_KEY = 'simulator.recent.v1'
const LIMIT_PER_KIND = 6
const listeners = new Set<() => void>()

// Cached by the raw stored string so useSyncExternalStore gets the same array until something changes
let cache: { raw: string | null; entries: RecentEntry[] } = { raw: null, entries: [] }

const parse = (raw: string | null): RecentEntry[] => {
    if (!raw) return []
    try {
        const entries: unknown = JSON.parse(raw)
        return Array.isArray(entries) ? (entries as RecentEntry[]).filter((entry) => entry && typeof entry.id === 'string' && typeof entry.kind === 'string') : []
    } catch {
        return []
    }
}

const notify = () => listeners.forEach((listener) => listener())

export default class RecentService {
    // Newest first
    static snapshot(): RecentEntry[] {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw !== cache.raw) cache = { raw, entries: parse(raw) }
        return cache.entries
    }

    static subscribe(listener: () => void): () => void {
        listeners.add(listener)
        window.addEventListener('storage', listener)
        return () => {
            listeners.delete(listener)
            window.removeEventListener('storage', listener)
        }
    }

    static record(entry: Omit<RecentEntry, 'region' | 'at'>) {
        const region = RegionService.get()
        const isSame = (each: RecentEntry) => each.kind === entry.kind && each.id === entry.id && each.region === region
        const entries = [{ ...entry, region, at: Date.now() }, ...RecentService.snapshot().filter((each) => !isSame(each))]

        // Keep the newest few per kind and region
        const counts = new Map<string, number>()
        const kept = entries.filter((each) => {
            const group = `${each.kind}:${each.region}`
            const count = (counts.get(group) ?? 0) + 1
            counts.set(group, count)
            return count <= LIMIT_PER_KIND
        })

        localStorage.setItem(STORAGE_KEY, JSON.stringify(kept))
        notify()
    }

    static clear() {
        localStorage.removeItem(STORAGE_KEY)
        notify()
    }
}
